import * as layout from '../markLayout.module.css';
import { MarkButton } from '../MarkControls';
import React from 'react';
import P from '@hero/ui-kit/typography/P';
import type { MarkVariable } from '../../types';
import { useEditorContext } from './EditorContext';
type Props = {
    interactive?: boolean;
};
type VariableEntry = {
    key: string;
    title: string;
};
const URL_SLUG_GROUP = 'Mark URLs';

const VariableList: React.FC<Props> = ({ interactive = true }) => {
    const { variables, urlSlugs, insertTarget, setInsertTarget } = useEditorContext();
    const handleInsert = (key: string) => {
        if (!insertTarget) return;
        if (insertTarget.personalization === 'links' && !urlSlugs.includes(key)) return;
        const { element, selectionStart, selectionEnd, onInsert } = insertTarget;
        const text = `{{${key}}}`;
        const current = element.value;
        const newValue =
            insertTarget.personalization === 'links'
                ? text
                : current.slice(0, selectionStart) + text + current.slice(selectionEnd);
        onInsert(newValue);
        const newPos =
            insertTarget.personalization === 'links' ? text.length : selectionStart + text.length;
        setInsertTarget({
            ...insertTarget,
            selectionStart: newPos,
            selectionEnd: newPos
        });
        requestAnimationFrame(() => {
            element.focus();
            element.setSelectionRange(newPos, newPos);
        });
    };
    if (variables.length === 0 && urlSlugs.length === 0) {
        return (
            <P
                noDefaultMargin
                size="small"
                styles={{
                    color: 'var(--mark-muted)'
                }}
            >
                No variables available
            </P>
        );
    }
    const grouped = variables.reduce<Record<string, VariableEntry[]>>((acc, v: MarkVariable) => {
        const group = v.owner || 'Other';
        if (!acc[group]) acc[group] = [];
        acc[group].push({ key: v.key, title: v.label });
        return acc;
    }, {});
    if (urlSlugs.length > 0) {
        grouped[URL_SLUG_GROUP] = urlSlugs.map((slug) => ({ key: slug, title: 'Mark URL' }));
    }
    return (
        <div>
            <P
                noDefaultMargin
                styles={{
                    fontWeight: 600,
                    marginBottom: '0.8rem'
                }}
            >
                Variables
            </P>
            {interactive && (
                <P
                    noDefaultMargin
                    size="small"
                    styles={{
                        color: 'var(--mark-muted)',
                        marginBottom: '0.8rem'
                    }}
                >
                    {insertTarget?.personalization === 'links'
                        ? 'Choose a saved link to replace the destination.'
                        : insertTarget
                          ? 'Click a variable to insert at cursor.'
                          : 'Focus a text field, then click to insert.'}
                </P>
            )}
            {Object.entries(grouped).map(([owner, vars]) => (
                <div
                    key={owner}
                    style={{
                        marginBottom: '1.2rem'
                    }}
                >
                    <P
                        noDefaultMargin
                        size="small"
                        styles={{
                            color: 'var(--mark-muted)',
                            marginBottom: '0.4rem',
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em'
                        }}
                    >
                        {owner}
                    </P>
                    {vars.map((v) =>
                        interactive ? (
                            <MarkButton
                                key={v.key}
                                type="button"
                                title={v.title}
                                disabled={
                                    !insertTarget ||
                                    (insertTarget.personalization === 'links' &&
                                        !urlSlugs.includes(v.key))
                                }
                                onClick={() => handleInsert(v.key)}
                                style={{
                                    display: 'block',
                                    width: '100%',
                                    textAlign: 'left',
                                    marginBottom: '0.3rem'
                                }}
                            >
                                {`{{${v.key}}}`}
                            </MarkButton>
                        ) : (
                            <div
                                key={v.key}
                                title={v.title}
                                style={{
                                    width: '100%',
                                    textAlign: 'left',
                                    fontFamily: 'monospace',
                                    backgroundColor: 'var(--mark-surface)',
                                    marginBottom: '0.3rem'
                                }}
                                className={layout.panel}
                            >
                                {`{{${v.key}}}`}
                            </div>
                        )
                    )}
                </div>
            ))}
        </div>
    );
};
export default VariableList;
